import React from 'react';

import { IconInfoCircle } from '@trqk/components';
import { constants, formatMailtoContent, locale } from '@trqk/utils';

const { SUPPORT_EMAIL_ADDRESS } = constants;

/**
 * A toast message shown when a statement could not be ingested, either due to a timeout or an
 * error.
 * @param {ToastRenderContentProps<IngestionToastInput>} props
 */
export const CouldNotIngest = ({
    input,
}: ToastRenderContentProps<IngestionToastInput>): JSX.Element => {
    const canRetry = input?.canRetry ?? false;
    const error = input?.error ?? null;
    const failureMessages = input?.failureMessages ?? null;

    const errorMessage =
        failureMessages !== null && failureMessages.length > 0
            ? failureMessages.join(' | ')
            : error;

    const filename = input?.filename ?? null;
    const statementUrl = input?.statementUrl ?? null;
    const statementView = input?.statementView ?? null;
    const title =
        input?.title ??
        locale.enUS.INGESTION.TOASTS.STATEMENT_FAILURE.COULD_NOT_INGEST.TITLE
            .NOT_ABLE_TO_INGEST;

    const bodyLines = [
        locale.enUS.INGESTION.TOASTS.STATEMENT_FAILURE.COULD_NOT_INGEST
            .REPORT_ISSUE_EMAIL.BODY.GREETING,
        locale.enUS.INGESTION.TOASTS.STATEMENT_FAILURE.COULD_NOT_INGEST
            .REPORT_ISSUE_EMAIL.BODY.PART_1,
        locale.enUS.INGESTION.TOASTS.STATEMENT_FAILURE.COULD_NOT_INGEST
            .REPORT_ISSUE_EMAIL.BODY.SIGN_OFF,
        '',
        '----------',
    ];

    if (filename) {
        bodyLines.push(`File name: ${filename}`);
    }

    if (statementUrl) {
        bodyLines.push(`Statement URL: ${statementUrl}`);
    }

    bodyLines.push(`Error message(s):\n${errorMessage}`);

    if (statementView) {
        bodyLines.push(
            `Statement data:\n${JSON.stringify(statementView, null, 4)}`,
        );
    }

    const mailtoContent = formatMailtoContent({
        email: SUPPORT_EMAIL_ADDRESS,
        subject:
            locale.enUS.INGESTION.TOASTS.STATEMENT_FAILURE.COULD_NOT_INGEST
                .REPORT_ISSUE_EMAIL.SUBJECT,
        bodyLines,
    });

    return (
        <div style={{ width: 410 }} className="px-2">
            <div className="font-bold text-highlight-orange tracking-wider mb-1">
                {title}
            </div>
            <div className="my-2">
                {
                    locale.enUS.INGESTION.TOASTS.STATEMENT_FAILURE
                        .COULD_NOT_INGEST.PART_1
                }
            </div>
            <div className="my-2">
                {
                    locale.enUS.INGESTION.TOASTS.STATEMENT_FAILURE
                        .COULD_NOT_INGEST.PART_2
                }
            </div>
            <div className="text-sm italic mt-2 mb-4 flex items-center">
                <IconInfoCircle
                    fillColor="tertiary-300"
                    width={20}
                    addClasses="mr-1"
                />
                {
                    locale.enUS.INGESTION.TOASTS.STATEMENT_FAILURE
                        .COULD_NOT_INGEST.REPORT_ISSUE
                }
                &nbsp;
                <a className="text-primary-500 underline" href={mailtoContent}>
                    {SUPPORT_EMAIL_ADDRESS}
                </a>
            </div>
            <hr />
            <div className="my-2">
                <div className="mt-4">
                    {!filename && (
                        <div className="font-semibold mt-4">
                            {
                                locale.enUS.INGESTION.TOASTS.STATEMENT_FAILURE
                                    .ERROR_MESSAGE
                            }
                        </div>
                    )}
                    {filename && (
                        <div className="mt-4">
                            <span className="font-semibold">
                                {
                                    locale.enUS.INGESTION.TOASTS
                                        .STATEMENT_FAILURE.COULD_NOT_INGEST
                                        .LABEL
                                }
                            </span>
                            <span>{`: ${filename}`}</span>
                        </div>
                    )}
                </div>
            </div>
            {canRetry && (
                <>
                    <hr />
                    <div className="mt-2 text-tertiary-400">
                        {
                            locale.enUS.INGESTION.TOASTS.STATEMENT_FAILURE
                                .COULD_NOT_INGEST.CAN_RETRY
                        }
                    </div>
                </>
            )}
        </div>
    );
};
